import Decorator from "@evertrue/client-decorator";
import { ACTIVITY_TYPES } from "./constants";

const ACTIVITY_TYPE_VALUES = ACTIVITY_TYPES.map(i => i.value);
const VOL_APP_ID = 35;

export const prospectsTotalQuery = ({ poolId, assigned }) => {
  return {
    must: [
      {
        parent_role: {
          match: "prospect",
        },
      },
      {
        pool_id: {
          match: poolId,
        },
      },
      ...(assigned !== "all"
        ? [
            {
              solicitor_contact_id: {
                exists: assigned,
              },
            },
          ]
        : []),
    ],
  };
};

export const stageStatsQuery = ({ poolId, stages, assigned, solicitorContactId }) => {
  const query = {
    must: [
      {
        pool_id: {
          match: poolId,
        },
      },
      ...(assigned !== "all" ? [{ solicitor_contact_id: { exists: assigned } }] : []),
      {
        parent_role: {
          match: "prospect",
        },
      },
      {
        "assignment_stage.untouched": {
          in: stages,
        },
      },
    ],
    stats: [
      {
        assignment_id: {
          bucket_by: "assignment_stage.untouched",
        },
      },
    ],
  };
  if (solicitorContactId) {
    query.must.push({
      solicitor_contact_id: { match: solicitorContactId },
    });
  }
  return query;
};

export const numberOfInteractions = ({ poolId, assigned, activityDateParameters }) => {
  const query = {
    ...(assigned !== "all" && {
      has_parent: [
        {
          type: "contact",
          query: {
            has_child: [
              {
                type: "assignment",
                query: {
                  must: [
                    {
                      parent_role: {
                        match: "prospect",
                      },
                    },
                    { solicitor_contact_id: { exists: assigned } },
                    {
                      pool_id: {
                        match: poolId,
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      ],
    }),

    must: [
      {
        label: {
          instance: {
            name: { match: "Volunteer Pool ID" },
            value: { match: poolId },
          },
        },
      },
      {
        updated_at: activityDateParameters,
      },
    ],
  };
  return query;
};

export const numberOfProspectsWithInteractions = ({ poolId, assigned, activityDateParameters, solicitorContactId }) => {
  const query = {
    has_child: [
      {
        type: "contact_note",
        query: {
          must: [
            {
              label: {
                instance: {
                  name: {
                    match: "Volunteer Pool ID",
                  },
                  value: {
                    match: poolId,
                  },
                },
              },
            },
            {
              updated_at: activityDateParameters,
            },
          ],
        },
      },
      ...(assigned !== "all"
        ? [
            {
              type: "assignment",
              query: {
                must: [
                  {
                    parent_role: {
                      match: "prospect",
                    },
                  },
                  { solicitor_contact_id: { exists: assigned } },
                  {
                    pool_id: {
                      match: poolId,
                    },
                  },
                ],
              },
            },
          ]
        : []),
    ],
  };
  if (solicitorContactId) {
    query.has_child.push({
      type: "assignment",
      query: {
        must: [
          {
            parent_role: {
              match: "prospect",
            },
          },
          {
            solicitor_contact_id: {
              match: solicitorContactId,
            },
          },
          {
            pool_id: {
              match: poolId,
            },
          },
        ],
      },
    });
  }

  return query;
};

export const activityTypeQuery = ({ poolId, assigned, activityDateParameters, solicitorUserId }) => {
  const query = {
    must: [
      {
        label: {
          instance: {
            name: {
              match: "Volunteer Pool ID",
            },
            value: {
              match: poolId,
            },
          },
        },
      },
      {
        "interaction_type.untouched": {
          in: ACTIVITY_TYPE_VALUES,
        },
      },
      {
        updated_at: activityDateParameters,
      },
    ],
    ...(assigned !== "all" && {
      has_parent: [
        {
          type: "contact",
          query: {
            has_child: [
              {
                type: "assignment",
                query: {
                  must: [
                    {
                      parent_role: {
                        match: "prospect",
                      },
                    },
                    { solicitor_contact_id: { exists: assigned } },
                    {
                      pool_id: {
                        match: poolId,
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      ],
    }),

    stats: [
      {
        id: {
          bucket_by: "interaction_type.untouched",
        },
      },
    ],
  };
  if (solicitorUserId) {
    query.must.push({
      creator_user_id: {
        match: solicitorUserId,
      },
    });
  }
  return query;
};

export const SOLICITORS_BY_NAME_QUERY = (name = "", pools = null) => ({
  sort: [
    {
      name_last: {
        order: "asc",
      },
    },
    {
      name_first: {
        order: "asc",
      },
    },
  ],
  ...Decorator.Filters.getQuery({ full_name: name }).toJSON(),

  has_child: [
    {
      type: "assignment",
      query: {
        must: [
          {
            parent_role: {
              match: "solicitor",
            },
          },
          ...(pools ? [{ pool_id: { in: pools } }] : []),
        ],
      },
    },
  ],
});

const getSorting = ({ sortOrder: order, sortBy }) => {
  if (order && sortBy) {
    const sorting = { sort: [{ [sortBy]: { order, ...(order === "asc" && { missing: "_first" }) } }] };
    return sorting;
  }
  return {};
};

export const getAnalyticsQuery = ({ poolId, name, sortOrder, sortBy }) => {
  const query = {
    must: [
      {
        pool_id: {
          match: poolId,
        },
      },
      ...Decorator.Filters.getQuery({ full_name: name }).toJSON().must,
    ],
    ...getSorting({ sortOrder, sortBy }),
  };

  return query;
};
export const contactByIdsQuery = ({ contactIds }) => ({
  must: [
    {
      id: {
        in: contactIds,
      },
    },
  ],
});

export const getVolunteerSessionsQuery = ({ contactId }) => {
  const query = {
    has_parent: [
      {
        query: {
          must: [
            {
              affiliations: {
                instance: {
                  contact_id: {
                    match: contactId,
                  },
                },
              },
            },
          ],
        },
        type: "user",
      },
    ],
    must: [
      {
        application_id: {
          match: VOL_APP_ID,
        },
      },
    ],
    sort: [
      {
        created_at: {
          order: "desc",
        },
      },
    ],
  };

  return query;
};
